import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import { asList } from '@/lib/api/list'

/**
 * Gider departmanları (İK departmanları): gider kaydı ve kasa hareketinde isteğe bağlı; boşsa kategorinin varsayılan
 * departmanı. Departman yevmiye satırına yansır; kâr/zarar departmana göre süzülür. Eski kayıtlara toplu atama yapılır.
 */
export const DEPT_PERMS = { assign: 'finance:department:assign' } as const
export const DEPARTMENT_NONE = 'none'

type Row = Record<string, unknown>
const s = (v: unknown) => (v == null ? '' : String(v))

export type DepartmentOption = { id: string; name: string; code: string }

export const useFinanceDepartments = (enabled = true) =>
  useQuery({
    queryKey: ['finance', 'departments'],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async () => asList(await api.get<Row[]>('/api/v1/finance/departments')).map((r): DepartmentOption => ({ id: s(r.id), name: s(r.name), code: s(r.code) })),
  })

export type UnassignedRecord = { kind: 'ledger' | 'cash'; id: string; date: string; description: string; category: string; amount: string }

export const useUnassigned = (kind: 'ledger' | 'cash', from: string, to: string) =>
  useQuery({
    queryKey: ['finance', 'departments', 'unassigned', kind, from, to],
    queryFn: async () =>
      asList(await api.get<Row[]>('/api/v1/finance/departments/unassigned', { kind, from, to })).map(
        (r): UnassignedRecord => ({ kind: r.kind === 'cash' ? 'cash' : 'ledger', id: s(r.id), date: s(r.date), description: s(r.description), category: s(r.category), amount: s(r.amount) }),
      ),
  })

export const useAssignDepartment = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ kind, ids, departmentId }: { kind: 'ledger' | 'cash'; ids: string[]; departmentId: string }) =>
      api.post<{ assigned: number }>('/api/v1/finance/departments/assign', { kind, ids, department_id: departmentId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['finance'] }),
  })
}

export const useCategoryDepartments = () =>
  useQuery({
    queryKey: ['finance', 'departments', 'category-defaults'],
    queryFn: async () => ((await api.get<Record<string, string>>('/api/v1/finance/departments/category-defaults')) ?? {}) as Record<string, string>,
  })

export const useSetCategoryDepartment = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ categoryId, departmentId }: { categoryId: string; departmentId: string }) =>
      api.put(`/api/v1/finance/categories/${categoryId}/department`, { department_id: departmentId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['finance', 'departments'] }),
  })
}

export type DepartmentTotal = { departmentId: string; name: string; income: string; expense: string; personnel: string | null; net: string }

export const usePnLDepartments = (f: { from: string; to: string; projectId: string; partyId: string }, enabled = true) =>
  useQuery({
    queryKey: ['finance', 'pnl', 'departments', f],
    enabled,
    queryFn: async () =>
      asList(await api.get<Row[]>('/api/v1/finance/reports/pnl/departments', { from: f.from, to: f.to, project_id: f.projectId, party_id: f.partyId })).map(
        (r): DepartmentTotal => ({
          departmentId: s(r.department_id),
          name: s(r.name),
          income: s(r.income),
          expense: s(r.expense),
          personnel: r.personnel == null ? null : s(r.personnel),
          net: s(r.net),
        }),
      ),
  })
